import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { toast } from 'sonner';
import {
  format,
  parseISO,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isWeekend,
  addMonths,
  subMonths,
  isSameDay,
  startOfYear,
  endOfYear,
} from 'date-fns';
import {
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  Calendar,
  Plus,
  Trash2,
  Edit,
  Briefcase,
  Palmtree,
  Umbrella,
  Heart,
  User,
  Baby,
  GraduationCap,
  PartyPopper,
  Settings,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Leave type icons mapping
const leaveTypeIcons = {
  annual: Umbrella,
  sick: Heart,
  personal: User,
  maternity: Baby,
  study_leave: GraduationCap,
  default: Palmtree,
};

const getLeaveIcon = (type) => leaveTypeIcons[type] || leaveTypeIcons.default;

const getLeaveColor = (type) => {
  const colors = {
    annual: 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300',
    sick: 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300',
    personal: 'bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300',
    maternity: 'bg-pink-100 text-pink-700 dark:bg-pink-900/20 dark:text-pink-300',
    study_leave: 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300',
    default: 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:bg-slate-50 dark:bg-slate-7000/20 dark:text-slate-300',
  };
  return colors[type] || colors.default;
};

const Holidays = () => {
  const { api, user, token } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Calendar state
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [activeTab, setActiveTab] = useState('calendar');

  // Settings state (admin only)
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveTypeModalOpen, setLeaveTypeModalOpen] = useState(false);
  const [newLeaveType, setNewLeaveType] = useState({ name: '', default_balance: 0, requires_approval: true });
  const [editLeaveTypeModalOpen, setEditLeaveTypeModalOpen] = useState(false);
  const [editingLeaveType, setEditingLeaveType] = useState(null);
  const [holidayModalOpen, setHolidayModalOpen] = useState(false);
  const [newHoliday, setNewHoliday] = useState({ date: '', name: '' });

  // Fetch holidays (all roles)
  const fetchHolidays = useCallback(async () => {
    if (!api) return;
    try {
      const res = await api.get('/time-off/holidays');
      setHolidays(res.data);
    } catch (err) {
      console.error('Failed to load holidays:', err);
      toast.error('Failed to load holidays');
    }
  }, [api]);

  // Fetch admin holidays (uses admin endpoint for CRUD)
  const fetchAdminHolidays = useCallback(async () => {
    if (!token || !isAdmin) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/time-off/holidays`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setHolidays(data);
    } catch (err) {
      console.error('Failed to fetch admin holidays:', err);
    }
  }, [token, isAdmin]);

  // Fetch leave types (admin only)
  const fetchLeaveTypes = useCallback(async () => {
    if (!token || !isAdmin) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/time-off/leave-types`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setLeaveTypes(data);
    } catch (err) {
      console.error('Failed to fetch leave types:', err);
    }
  }, [token, isAdmin]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      if (isAdmin) {
        await Promise.all([fetchAdminHolidays(), fetchLeaveTypes()]);
      } else {
        await fetchHolidays();
      }
      setLoading(false);
    };
    if (api || token) load();
  }, [api, token, isAdmin, fetchHolidays, fetchAdminHolidays, fetchLeaveTypes]);

  // --- Leave Type Handlers ---
  const handleCreateLeaveType = async () => {
    if (!newLeaveType.name) { toast.error('Leave type name is required'); return; }
    try {
      const res = await fetch(`${API_URL}/api/admin/time-off/leave-types`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newLeaveType),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Failed'); }
      toast.success('Leave type created');
      setLeaveTypeModalOpen(false);
      setNewLeaveType({ name: '', default_balance: 0, requires_approval: true });
      fetchLeaveTypes();
    } catch (err) { toast.error(err.message); }
  };

  const handleDeleteLeaveType = async (typeId) => {
    if (!confirm('Are you sure you want to delete this leave type?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/time-off/leave-types/${typeId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Failed'); }
      toast.success('Leave type deleted');
      fetchLeaveTypes();
    } catch (err) { toast.error(err.message); }
  };

  const handleEditLeaveType = (type) => {
    setEditingLeaveType({
      ...type,
      default_balance: type.default_balance || 0,
      requires_approval: type.requires_approval !== false,
      is_active: type.is_active !== false,
    });
    setEditLeaveTypeModalOpen(true);
  };

  const handleSaveLeaveType = async () => {
    if (!editingLeaveType) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/time-off/leave-types/${editingLeaveType.id || editingLeaveType.key}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          default_balance: editingLeaveType.default_balance,
          requires_approval: editingLeaveType.requires_approval,
          is_active: editingLeaveType.is_active,
        }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Failed'); }
      toast.success('Leave type updated');
      setEditLeaveTypeModalOpen(false);
      setEditingLeaveType(null);
      fetchLeaveTypes();
    } catch (err) { toast.error(err.message); }
  };

  // --- Holiday Handlers ---
  const handleCreateHoliday = async () => {
    if (!newHoliday.date || !newHoliday.name) { toast.error('Date and name are required'); return; }
    try {
      const res = await fetch(`${API_URL}/api/admin/time-off/holidays`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newHoliday),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Failed'); }
      toast.success('Public holiday added');
      setHolidayModalOpen(false);
      setNewHoliday({ date: '', name: '' });
      fetchAdminHolidays();
    } catch (err) { toast.error(err.message); }
  };

  const handleDeleteHoliday = async (holidayId) => {
    if (!confirm('Are you sure you want to delete this holiday?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/time-off/holidays/${holidayId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Failed'); }
      toast.success('Holiday deleted');
      fetchAdminHolidays();
    } catch (err) { toast.error(err.message); }
  };

  // --- Calendar Memos ---
  const allHolidaysThisYear = useMemo(() => {
    const ys = startOfYear(new Date());
    const ye = endOfYear(new Date());
    return holidays
      .filter((h) => { const d = parseISO(h.date); return d >= ys && d <= ye; })
      .sort((a, b) => parseISO(a.date) - parseISO(b.date));
  }, [holidays]);

  const upcomingHolidays = useMemo(() => {
    const now = new Date();
    return holidays
      .filter((h) => parseISO(h.date) >= now)
      .sort((a, b) => parseISO(a.date) - parseISO(b.date));
  }, [holidays]);

  const calendarDays = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const days = eachDayOfInterval({ start, end });
    return [...Array(start.getDay()).fill(null), ...days];
  }, [currentMonth]);

  const holidayMap = useMemo(() => {
    const m = {};
    holidays.forEach((h) => { m[h.date] = h; });
    return m;
  }, [holidays]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <TooltipProvider delayDuration={300}>
        <div className="space-y-6" data-testid="holidays-page">
          {/* Header */}
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
              Holidays
            </h1>
            <p className="text-slate-500 mt-1">
              Company holidays and public holidays for this year
            </p>
          </div>

          {/* Next holiday banner */}
          {upcomingHolidays.length > 0 && (
            <div className="premium-card p-4 flex items-center gap-4" data-testid="next-holiday-banner">
              <div className="w-12 h-12 rounded-xl bg-red-50 dark:bg-red-900/10 flex items-center justify-center">
                <CalendarDays className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider font-medium">Next Holiday</p>
                <p className="text-lg font-bold text-slate-900 dark:text-white">{upcomingHolidays[0].name}</p>
                <p className="text-sm text-red-500 font-medium">
                  {format(parseISO(upcomingHolidays[0].date), 'EEEE, MMMM d, yyyy')}
                </p>
              </div>
            </div>
          )}

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList>
              <TabsTrigger value="calendar" data-testid="holidays-tab-calendar">
                <Calendar className="w-4 h-4 mr-2" />
                Calendar
              </TabsTrigger>
              {isAdmin && (
                <TabsTrigger value="settings" data-testid="holidays-tab-settings">
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </TabsTrigger>
              )}
            </TabsList>

            {/* CALENDAR TAB */}
            <TabsContent value="calendar" className="mt-0">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Calendar */}
                <div className="lg:col-span-2">
                  <div className="premium-card p-5" data-testid="holiday-calendar">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-base font-semibold text-slate-900 dark:text-white">
                        {format(currentMonth, 'MMMM yyyy')}
                      </h3>
                      <div className="flex items-center gap-1">
                        <Button variant="outline" size="icon" onClick={() => setCurrentMonth((p) => subMonths(p, 1))} className="h-8 w-8 rounded-lg" data-testid="holiday-prev-month">
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setCurrentMonth(new Date())} className="h-8 rounded-lg text-xs px-3" data-testid="holiday-today-btn">
                          Today
                        </Button>
                        <Button variant="outline" size="icon" onClick={() => setCurrentMonth((p) => addMonths(p, 1))} className="h-8 w-8 rounded-lg" data-testid="holiday-next-month">
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-7 gap-1">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                        <div key={d} className="text-center text-xs font-medium text-slate-500 py-2">{d}</div>
                      ))}
                      {calendarDays.map((day, idx) => {
                        if (!day) return <div key={`e-${idx}`} className="h-10" />;
                        const dateStr = format(day, 'yyyy-MM-dd');
                        const holiday = holidayMap[dateStr];
                        const isToday = isSameDay(day, new Date());
                        const isWkend = isWeekend(day);
                        let bg = '', text = 'text-slate-900 dark:text-white', ring = '';
                        if (isWkend) { bg = 'bg-slate-50 dark:bg-slate-800/30'; text = 'text-slate-400'; }
                        if (holiday) { bg = 'bg-red-500 dark:bg-red-600'; text = 'text-white font-semibold'; }
                        if (isToday) ring = holiday ? '' : 'ring-2 ring-blue-500';
                        return (
                          <Tooltip key={day.toISOString()}>
                            <TooltipTrigger asChild>
                              <div className={`h-10 flex items-center justify-center rounded-lg text-sm cursor-default transition-colors ${bg} ${text} ${ring}`} data-testid={holiday ? `holiday-day-${dateStr}` : undefined}>
                                {format(day, 'd')}
                              </div>
                            </TooltipTrigger>
                            {holiday && <TooltipContent><p className="text-xs font-medium">{holiday.name}</p></TooltipContent>}
                          </Tooltip>
                        );
                      })}
                    </div>

                    <div className="flex items-center gap-4 mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                      <div className="flex items-center gap-1.5 text-xs text-slate-500"><div className="w-3 h-3 rounded bg-red-500 dark:bg-red-600" /><span>Holiday</span></div>
                      <div className="flex items-center gap-1.5 text-xs text-slate-500"><div className="w-3 h-3 rounded bg-slate-100 dark:bg-slate-700" /><span>Weekend</span></div>
                      <div className="flex items-center gap-1.5 text-xs text-slate-500"><div className="w-3 h-3 rounded ring-2 ring-blue-500" /><span>Today</span></div>
                    </div>
                  </div>
                </div>

                {/* Holiday List */}
                <div className="lg:col-span-1">
                  <div className="premium-card p-5 h-full" data-testid="holiday-list">
                    <div className="flex items-center gap-2 mb-4">
                      <Calendar className="w-4 h-4 text-red-500" />
                      <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                        All Holidays ({new Date().getFullYear()})
                      </h3>
                    </div>
                    {allHolidaysThisYear.length === 0 ? (
                      <p className="text-sm text-slate-400 py-4" data-testid="no-holidays-msg">No holidays configured for this year</p>
                    ) : (
                      <div className="space-y-1 max-h-[500px] overflow-y-auto">
                        {allHolidaysThisYear.map((h, idx) => {
                          const hDate = parseISO(h.date);
                          const isPast = hDate < new Date() && !isSameDay(hDate, new Date());
                          const isTodayH = isSameDay(hDate, new Date());
                          return (
                            <div key={h.id || idx} className={`flex items-center justify-between py-2.5 px-3 rounded-lg transition-colors ${isTodayH ? 'bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-500/20' : isPast ? 'opacity-50' : 'hover:bg-slate-50 dark:hover:bg-slate-800/40'}`} data-testid={`holiday-item-${h.date}`}>
                              <div className="flex items-center gap-3 min-w-0">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 ${isTodayH ? 'bg-red-500 dark:bg-red-600 text-white' : 'bg-red-50 dark:bg-red-900/10 text-red-500'}`}>
                                  {format(hDate, 'd')}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate">{h.name}</p>
                                  <p className="text-xs text-slate-500">{format(hDate, 'EEE, MMM d')}</p>
                                </div>
                              </div>
                              {isTodayH && <span className="text-[10px] bg-red-500 dark:bg-red-600 text-white px-2 py-0.5 rounded-full font-medium ml-2 flex-shrink-0">Today</span>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* SETTINGS TAB (Admin only) */}
            {isAdmin && (
              <TabsContent value="settings" className="space-y-6">
                {/* Leave Types Section */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Briefcase className="w-5 h-5" />
                        Leave Types
                      </CardTitle>
                      <Button onClick={() => setLeaveTypeModalOpen(true)} data-testid="add-leave-type-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Leave Type
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-3">
                      {leaveTypes.map((type) => {
                        const LeaveIcon = getLeaveIcon(type.key);
                        return (
                          <div key={type.id || type.key} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`leave-type-${type.key}`}>
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-lg ${getLeaveColor(type.key)}`}>
                                <LeaveIcon className="w-4 h-4" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-slate-900 dark:text-white">{type.name}</p>
                                  {type.is_system && <Badge variant="outline" className="text-slate-400 text-xs">System</Badge>}
                                </div>
                                <p className="text-xs text-slate-500">
                                  Default: {type.default_balance} days
                                  {type.is_active === false && ' \u2022 Inactive'}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button variant="ghost" size="sm" className="text-slate-600 hover:text-slate-700 hover:bg-slate-50 dark:bg-slate-700" onClick={() => handleEditLeaveType(type)} data-testid={`edit-leave-type-${type.id || type.key}`}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:bg-red-900/30" onClick={() => handleDeleteLeaveType(type.id || type.key)} data-testid={`delete-leave-type-${type.id || type.key}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                      {leaveTypes.length === 0 && (
                        <p className="text-sm text-slate-400 py-4 text-center">No leave types configured</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Public Holidays Section */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <PartyPopper className="w-5 h-5" />
                        Public Holidays
                      </CardTitle>
                      <Button onClick={() => setHolidayModalOpen(true)} data-testid="add-holiday-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Holiday
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {holidays.length > 0 ? (
                      <div className="grid gap-3">
                        {holidays.map((holiday) => (
                          <div key={holiday.id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`setting-holiday-${holiday.id}`}>
                            <div className="flex items-center gap-3">
                              <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400">
                                <CalendarDays className="w-4 h-4" />
                              </div>
                              <div>
                                <p className="font-medium text-slate-900 dark:text-white">{holiday.name}</p>
                                <p className="text-xs text-slate-500">{format(parseISO(holiday.date), 'EEEE, MMMM d, yyyy')}</p>
                              </div>
                            </div>
                            <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:bg-red-900/30" onClick={() => handleDeleteHoliday(holiday.id)} data-testid={`delete-holiday-${holiday.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        <PartyPopper className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                        <p>No public holidays configured</p>
                        <Button variant="link" onClick={() => setHolidayModalOpen(true)}>Add your first holiday</Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            )}
          </Tabs>
        </div>

        {/* MODALS */}

        {/* Add Leave Type Modal */}
        <Dialog open={leaveTypeModalOpen} onOpenChange={setLeaveTypeModalOpen}>
          <DialogContent data-testid="leave-type-modal">
            <DialogHeader>
              <DialogTitle>Add Custom Leave Type</DialogTitle>
              <DialogDescription>Create a new leave type for your organization</DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input value={newLeaveType.name} onChange={(e) => setNewLeaveType({ ...newLeaveType, name: e.target.value })} placeholder="e.g., Maternity Leave, Study Leave" data-testid="leave-type-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Default Balance (days)</Label>
                <Input type="number" min="0" value={newLeaveType.default_balance} onChange={(e) => setNewLeaveType({ ...newLeaveType, default_balance: parseInt(e.target.value) || 0 })} data-testid="leave-type-balance-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setLeaveTypeModalOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateLeaveType} data-testid="save-leave-type-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Leave Type
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Leave Type Modal */}
        <Dialog open={editLeaveTypeModalOpen} onOpenChange={setEditLeaveTypeModalOpen}>
          <DialogContent data-testid="edit-leave-type-modal">
            <DialogHeader>
              <DialogTitle>Edit Leave Type</DialogTitle>
              <DialogDescription>Update settings for {editingLeaveType?.name}</DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div className="space-y-2">
                <Label>Default Balance (days)</Label>
                <Input type="number" min="0" value={editingLeaveType?.default_balance || 0} onChange={(e) => setEditingLeaveType({ ...editingLeaveType, default_balance: parseInt(e.target.value) || 0 })} data-testid="edit-leave-balance-input" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="edit_requires_approval" checked={editingLeaveType?.requires_approval || false} onChange={(e) => setEditingLeaveType({ ...editingLeaveType, requires_approval: e.target.checked })} className="rounded" />
                <Label htmlFor="edit_requires_approval">Requires manager approval</Label>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="edit_is_active" checked={editingLeaveType?.is_active !== false} onChange={(e) => setEditingLeaveType({ ...editingLeaveType, is_active: e.target.checked })} className="rounded" />
                <Label htmlFor="edit_is_active">Active</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditLeaveTypeModalOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveLeaveType} data-testid="save-edit-leave-type-btn">Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Holiday Modal */}
        <Dialog open={holidayModalOpen} onOpenChange={setHolidayModalOpen}>
          <DialogContent data-testid="holiday-modal">
            <DialogHeader>
              <DialogTitle>Add Public Holiday</DialogTitle>
              <DialogDescription>Add a public holiday for your organization</DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div className="space-y-2">
                <Label>Holiday Name *</Label>
                <Input value={newHoliday.name} onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })} placeholder="e.g., Christmas Day, Independence Day" data-testid="holiday-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input type="date" value={newHoliday.date} onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })} data-testid="holiday-date-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setHolidayModalOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateHoliday} data-testid="save-holiday-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Holiday
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </TooltipProvider>
    </Layout>
  );
};

export default Holidays;
